import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet";
import { toast } from "react-toastify";
import {
  FiFileText,
  FiUser,
  FiMail,
  FiPhone,
  FiLink,
  FiSend,
  FiAlertTriangle,
  FiHelpCircle,
  FiCheckSquare,
  FiUploadCloud,
} from "react-icons/fi";
import Header from "../../components/layouts/Header";
import Footer from "../../components/layouts/Footer";
import ContactStrip from "./ContactStrip";
import "./FooterPages.css";

const COMPLAINT_REASONS = [
  "belongs to another person and to which the user does not have any right",
  "is defamatory, obscene, pornographic, paedophilic, invasive of another's privacy, including bodily privacy, insulting or harassing on the basis of gender, libellous, racially or ethnically objectionable, relating or encouraging money laundering, orgambling, or otherwise inconsistent with or contrary to the laws in force",
  "is harmful to child",
  "infringes any patent, trademark, copyright or other proprietary rights",
  "violates any law for the time being in force",
  "deceives or misleads the addressee about the origin of the message or knowingly and intentionally communicates any information which is patently false or misleading in nature but may reasonably be perceived as a fact",
  "impersonates another person",
  "threatens the unity, integrity, defence, security or sovereignty of India, friendly relations with foreign States, or public order, or causes incitement to the commission of any cognisable offence or prevents investigation of any offence or is insulting other nation",
  "contains software virus or any other computer code, file or program designed to interrupt, destroy or limit the functionality of any computer resource",
  "is patently false and untrue, and is written or published in any form, with the intent to mislead or harass a person, entity or agency for financial gain or to cause any injury to any person",
  "Other",
];

const SummonsNotices = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    mobile: "",
    portal: "Mygoldenhomes.com (Web & Mobile)",
    pageUrl: "",
    reason: "",
    details: "",
    file: null,
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value, type, files } = e.target;
    if (type === "file") {
      setFormData((prev) => ({ ...prev, file: files[0] }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleReasonChange = (reasonText) => {
    setFormData((prev) => ({ ...prev, reason: reasonText }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name.trim()) {
      toast.error("Please enter your name");
      return;
    }
    if (!formData.email.trim() || !formData.email.includes("@")) {
      toast.error("Please enter a valid email address");
      return;
    }
    if (!formData.mobile.trim()) {
      toast.error("Please enter your contact number");
      return;
    }
    if (!formData.pageUrl.trim()) {
      toast.error("Please share the link (URL) of the reported page");
      return;
    }
    if (!formData.reason) {
      toast.error("Please choose a complaint reason");
      return;
    }
    if (!formData.details.trim()) {
      toast.error("Please describe your complaint in detail");
      return;
    }

    setIsSubmitting(true);

    try {
      await new Promise((res) => setTimeout(res, 900));

      toast.success("Complaint submitted successfully. Grievance Reference #MGH-GR-" + Math.floor(100000 + Math.random() * 900000), {
        position: "top-center",
        autoClose: 4000,
      });

      setFormData({
        name: "",
        email: "",
        mobile: "",
        portal: "Mygoldenhomes.com (Web & Mobile)",
        pageUrl: "",
        reason: "",
        details: "",
        file: null,
      });
    } catch (err) {
      toast.error("Failed to submit grievance. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="mgh-info-page-wrapper">
      <Helmet>
        <title>Summons &amp; Notices | Grievance Officer | My Golden Homes</title>
        <meta
          name="description"
          content="Grievance form to report complaints related to content, legal notices, and consumer issues on My Golden Homes."
        />
      </Helmet>

      {/* Unchanged Header */}
      <Header />

      {/* Hero Banner */}
      <section className="mgh-info-hero">
        <div className="mgh-info-hero-inner">
          <div className="mgh-info-hero-left">
            <div className="mgh-info-breadcrumb">
              <Link to="/">Home</Link>
              <span>&gt;</span>
              <span>Summons &amp; Notices</span>
            </div>
            <h1 className="mgh-info-hero-title">
              Summons &amp; <span>Notices</span>
            </h1>
            <p className="mgh-info-hero-subtitle">
              Use this form to report complaints related to content and consumer issues on My Golden Homes. Please provide accurate details to help us take appropriate action.
            </p>
          </div>

          <div className="mgh-info-hero-art">
            <img
              src="/assets/images/villa_header_banner.jpg"
              alt="My Golden Homes Grievance"
              onError={(e) => {
                e.target.onerror = null;
                e.target.src = "/assets/images/why_choose_villa.jpg";
              }}
            />
          </div>
        </div>
      </section>

      {/* Main Container */}
      <div className="mgh-info-container">
        <div className="mgh-two-col-layout">
          {/* Left Form Card */}
          <div className="mgh-form-card">
            <div className="mgh-form-card-header">
              <h3 style={{ fontSize: "18px", color: "#ea580c" }}>
                Grievance Form - for Complaints relating to content and Consumer Complaints.
              </h3>
              <p style={{ color: "#ef4444", fontSize: "12px", marginTop: "4px" }}>* Compulsory Fields</p>
            </div>

            <form onSubmit={handleSubmit}>
              <div className="mgh-form-group-item">
                <label>
                  * Your Name: <span className="req">*</span>
                </label>
                <div className="mgh-form-input-wrap">
                  <FiUser className="mgh-form-input-icon" />
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="Enter your name"
                    className="mgh-form-input"
                    required
                  />
                </div>
              </div>

              <div className="mgh-form-group-item">
                <label>
                  * Your E-Mail ID: <span className="req">*</span>
                </label>
                <div className="mgh-form-input-wrap">
                  <FiMail className="mgh-form-input-icon" />
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="Enter your email address"
                    className="mgh-form-input"
                    required
                  />
                </div>
              </div>

              <div className="mgh-form-group-item">
                <label>
                  * Your Contact Number: <span className="req">*</span>
                </label>
                <div className="mgh-form-input-wrap">
                  <FiPhone className="mgh-form-input-icon" />
                  <input
                    type="tel"
                    name="mobile"
                    value={formData.mobile}
                    onChange={handleChange}
                    placeholder="Enter your contact number"
                    className="mgh-form-input"
                    maxLength={10}
                    required
                  />
                </div>
              </div>

              <div className="mgh-form-group-item">
                <label>
                  * Please mark the web portal which hosts the Issue(s) encountered by you: <span className="req">*</span>
                </label>
                <select
                  name="portal"
                  value={formData.portal}
                  onChange={handleChange}
                  className="mgh-form-select"
                >
                  <option value="Mygoldenhomes.com (Web & Mobile)">Mygoldenhomes.com (Web &amp; Mobile)</option>
                  <option value="My Golden Homes Android Mobile App">My Golden Homes Android Mobile App</option>
                  <option value="My Golden Homes iOS Mobile App">My Golden Homes iOS Mobile App</option>
                </select>
              </div>

              <div className="mgh-form-group-item">
                <label>
                  * Please share the link (URL) of the Page which you are reporting against: <span className="req">*</span>
                </label>
                <div className="mgh-form-input-wrap">
                  <FiLink className="mgh-form-input-icon" />
                  <input
                    type="url"
                    name="pageUrl"
                    value={formData.pageUrl}
                    onChange={handleChange}
                    placeholder="Enter the page URL (e.g. https://www.mygoldenhomes.com/property-info/...)"
                    className="mgh-form-input"
                    required
                  />
                </div>
              </div>

              {/* Checkboxes List */}
              <div className="mgh-form-group-item">
                <label style={{ lineHeight: "1.4" }}>
                  * Please tell us the reason for your complaint/concern. Choose an option which most closely matches with your concern: <span className="req">*</span>
                </label>
                <div style={{ display: "flex", flexDirection: "column", gap: "8px", marginTop: "8px" }}>
                  {COMPLAINT_REASONS.map((reason, idx) => (
                    <label
                      key={idx}
                      style={{
                        display: "flex",
                        alignItems: "flex-start",
                        gap: "10px",
                        fontSize: "12.5px",
                        color: "#334155",
                        lineHeight: "1.45",
                        cursor: "pointer",
                      }}
                    >
                      <input
                        type="radio"
                        name="complaintReason"
                        checked={formData.reason === reason}
                        onChange={() => handleReasonChange(reason)}
                        style={{ marginTop: "3px", accentColor: "#0284c7" }}
                      />
                      <span>{reason}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Description */}
              <div className="mgh-form-group-item">
                <label>
                  * Please describe your complaint/concern in detail: <span className="req">*</span>
                </label>
                <textarea
                  name="details"
                  value={formData.details}
                  onChange={handleChange}
                  placeholder="Type your detailed description here..."
                  className="mgh-form-textarea"
                  rows={4}
                  required
                />
              </div>

              {/* File upload */}
              <div className="mgh-form-group-item">
                <label>
                  Please upload any supporting document(s) pertaining to the issue you are reporting:
                </label>
                <input
                  type="file"
                  name="file"
                  onChange={handleChange}
                  accept=".gif,.png,.jpg,.jpeg,.pdf"
                  style={{ fontSize: "13px" }}
                />
                <span style={{ display: "block", fontSize: "11px", color: "#64748b", marginTop: "4px" }}>
                  (Please upload a GIF, PNG, JPG, PDF or JPEG file only and ensure that the file is currently not in use/File Size Limit 2 MB)
                </span>
              </div>

              <button
                type="submit"
                className="mgh-btn-submit-primary"
                disabled={isSubmitting}
                style={{ marginTop: "16px" }}
              >
                {isSubmitting ? (
                  <span>Submitting Complaint...</span>
                ) : (
                  <>
                    <FiSend />
                    <span>Submit Complaint</span>
                  </>
                )}
              </button>
            </form>
          </div>

          {/* Right Column: Info Cards */}
          <div>
            {/* Card 1: Important Notice */}
            <div
              style={{
                background: "#fffbeb",
                border: "1px solid #fde68a",
                borderRadius: "12px",
                padding: "18px 20px",
                marginBottom: "20px",
              }}
            >
              <div style={{ display: "flex", gap: "12px", alignItems: "center", marginBottom: "8px" }}>
                <FiAlertTriangle style={{ color: "#d97706", fontSize: "20px" }} />
                <h4 style={{ margin: 0, fontSize: "15px", fontWeight: "700", color: "#92400e" }}>
                  Important Notice
                </h4>
              </div>
              <p style={{ margin: 0, fontSize: "13px", color: "#78350f", lineHeight: "1.5" }}>
                Misuse of this form or filing false complaints may lead to legal action. Please ensure that the details provided are accurate and genuine.
              </p>
            </div>

            {/* Card 2: Need Help */}
            <div
              style={{
                background: "#ffffff",
                border: "1px solid #e2e8f0",
                borderRadius: "12px",
                padding: "20px",
                marginBottom: "20px",
                boxShadow: "0 4px 12px rgba(15, 23, 42, 0.05)",
              }}
            >
              <div style={{ display: "flex", gap: "10px", alignItems: "center", marginBottom: "10px" }}>
                <FiHelpCircle style={{ color: "#0284c7", fontSize: "20px" }} />
                <h4 style={{ margin: 0, fontSize: "16px", fontWeight: "700", color: "#0f172a" }}>Need Help?</h4>
              </div>
              <p style={{ margin: "0 0 12px 0", fontSize: "13px", color: "#64748b" }}>
                If you have any questions about filing a complaint, please contact us.
              </p>
              <div style={{ fontSize: "13px", color: "#334155", lineHeight: "1.6" }}>
                <div><strong>Toll Free:</strong> +91 90634 83711</div>
                <div><strong>UAE:</strong> +91 906348 37119</div>
                <div><strong>USA/UK:</strong> 1800-41-99099</div>
              </div>
            </div>

            {/* Card 3: Guidelines */}
            <div
              style={{
                background: "#ffffff",
                border: "1px solid #e2e8f0",
                borderRadius: "12px",
                padding: "20px",
                boxShadow: "0 4px 12px rgba(15, 23, 42, 0.05)",
              }}
            >
              <div style={{ display: "flex", gap: "10px", alignItems: "center", marginBottom: "12px" }}>
                <FiCheckSquare style={{ color: "#059669", fontSize: "20px" }} />
                <h4 style={{ margin: 0, fontSize: "16px", fontWeight: "700", color: "#0f172a" }}>Guidelines</h4>
              </div>
              <ul style={{ margin: 0, paddingLeft: "18px", fontSize: "13px", color: "#475569", lineHeight: "1.6" }}>
                <li>Provide accurate and complete information.</li>
                <li>Ensure the content/link is accessible publicly.</li>
                <li>Upload valid supporting documents (if any).</li>
                <li>Do not submit false or malicious complaints.</li>
                <li>We may contact you for additional information if required.</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Contact Strip */}
        <ContactStrip />
      </div>

      {/* Unchanged Footer */}
      <Footer />
    </div>
  );
};

export default SummonsNotices;